import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'
import { downloadBlobAsFile } from 'src/utils/download-utils'
import { competenciaKey, formatCompetencia, formatMonthLabel, parseNonNegativeDecimal } from 'src/utils/formatter-utils'

import { WaitingScope } from '../../the-connection/tc_scopes'
import BillingKeys from './bl_keys'
import { BillingHeaderScope, BillingInvoiceDialogScope, BillingScope } from './bl_scopes'
import { BillingService } from './bl_service'
import type {
    BillingEntityType,
    BillingFilter,
    BillingLine,
    BillingOriginQuery,
    BillingPeriodRow,
    BillingUserLine
} from './bl_types'

const LOG = Logger.get('BillingPresenter')
const billingService = BillingService.singleton()

const DEFAULT_RANGE_MONTHS = 12

const MAX_MONTH_OPTIONS = 120

function toCentavos(value: number): number {
    return Math.round(value * 100)
}

const NO_MONTH = 'all'

export class BillingPresenter extends CubePresenter<MainPresenter, BillingScope> {
    private readonly __headerBar: Presenter<BillingHeaderScope, BillingPresenter>

    #allLines: BillingLine[] = []

    #appliedStartMonth = ''
    #appliedEndMonth = ''

    #originsRequest = 0

    public constructor(app: MainPresenter) {
        super(app, new BillingScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new BillingKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope
        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize(keys)
        }

        if (last) {
            keys.dialogSlot(undefined)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new BillingKeys(this.app, intent)
        const { filterPane } = this.scope

        keys.startMonth = filterPane.startMonth || undefined
        keys.endMonth = filterPane.endMonth || undefined
        keys.month = filterPane.month ?? NO_MONTH
        keys.groupId = filterPane.groupId
        keys.onlyExceeded = filterPane.onlyExceeded || undefined
    }

    async #initialize(keys: BillingKeys) {
        const { filterPane } = this.scope

        this.scope.monthly.caption = 'Excedente e valor por competência'

        filterPane.startMonth = keys.startMonth || defaultStartMonth()
        filterPane.endMonth = keys.endMonth || currentMonth()
        filterPane.groupId = keys.groupId

        const month = keys.month
        filterPane.month = month === NO_MONTH ? undefined : month || currentMonth()
        filterPane.onlyExceeded = keys.onlyExceeded ?? false

        filterPane.onToggleExpanded = this.action(this.handleToggleExpanded)
        filterPane.onStartMonthChanged = this.action(this.handleStartMonthChanged)
        filterPane.onEndMonthChanged = this.action(this.handleEndMonthChanged)
        filterPane.onMonthChanged = this.action(this.handleMonthChanged)
        filterPane.onOnlyExceededChanged = this.action(this.handleOnlyExceededChanged)
        filterPane.onGroupChanged = this.action(this.handleGroupChanged)
        filterPane.onApply = this.action(this.handleApply)
        filterPane.onClear = this.action(this.handleClear)

        this.scope.monthly.onPeriodClicked = this.action(this.handlePeriodClicked)

        this.scope.onSelectLine = this.action(this.handleSelectLine)
        this.scope.onInvoiceSelected = this.action(this.handleInvoiceSelected)
        this.scope.onReverseSelected = this.action(this.handleReverseSelected)
        this.scope.onDownload = this.action(this.handleDownload)

        this.scope.origins.onByUserChanged = this.action(this.handleOriginsByUserChanged)
        this.scope.origins.onByVersionChanged = this.action(this.handleOriginsByVersionChanged)
        this.scope.origins.onDownload = this.action(this.handleOriginsDownload)

        await this.#loadFilterOptions()
        await this.#load()
    }

    async #loadFilterOptions() {
        try {
            const options = await billingService.fetchFilterOptions()
            this.scope.filterPane.groupOptions = options.groups
        } catch (caught) {
            LOG.error('loadFilterOptions', caught)
        }
    }

    async #load() {
        const { filterPane } = this.scope

        this.#appliedStartMonth = competenciaKey(filterPane.startMonth) ?? ''
        this.#appliedEndMonth = competenciaKey(filterPane.endMonth) ?? ''

        filterPane.monthOptions = enumerateMonths(this.#appliedStartMonth, this.#appliedEndMonth)

        if (filterPane.month && !filterPane.monthOptions.includes(filterPane.month)) {
            filterPane.month = undefined
        }

        try {
            this.scope.waiting = new WaitingScope()

            this.#allLines = await billingService.fetchReport(this.#rangeFilter())

            this.scope.monthly.rows = buildPeriodRows(this.#allLines)
            this.scope.monthly.loaded = true

            this.#applyMonthSelection()
        } catch (caught) {
            LOG.error('load', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }

        await this.#reloadDetail()
    }

    #applyMonthSelection() {
        const { month } = this.scope.filterPane

        this.scope.monthly.selectedPeriod = month

        const doPeriodo = month
            ? this.#allLines.filter((line) => competenciaKey(line.mesAno) === month)
            : [...this.#allLines]

        const visiveis = this.scope.filterPane.onlyExceeded
            ? doPeriodo.filter((line) => line.qtdExcedente > 0)
            : doPeriodo

        this.scope.lines = visiveis

        if (this.scope.selectedPeriod && !visiveis.some((line) => this.#isSelectedLine(line))) {
            this.scope.selectedPeriod = undefined
        }

        this.#syncSelectedLine()

        const summary = summarize(doPeriodo)
        this.scope.totalExportado = summary.qtdExportada
        this.scope.totalExcedente = summary.qtdExcedente
        this.scope.valorAFaturar = summary.valorAFaturar
        this.scope.valorFaturado = summary.valorFaturado
        this.scope.linhasFaturadas = summary.linhasFaturadas
        this.scope.valorCortesia = summary.valorCortesia
        this.scope.linhasComCortesia = summary.linhasComCortesia
        this.scope.entidadesComExcedente = summary.entidadesComExcedente
        this.scope.entidadesSemTabela = summary.entidadesSemTabela
    }

    async #selectMonth(month?: string) {
        const { filterPane } = this.scope

        if (filterPane.month === month) {
            return
        }

        filterPane.month = month

        this.#applyMonthSelection()
        this.#syncIsFiltering()
        this.updateHistory()

        await this.#reloadDetail()
    }

    #syncSelectedLine() {
        this.scope.selectedLine = this.scope.lines.find((line) => this.#isSelectedLine(line))
    }

    #isSelectedLine(line: BillingLine): boolean {
        return (
            line.entityType === this.scope.selectedEntityType &&
            line.entityId === this.scope.selectedEntityId &&
            competenciaKey(line.mesAno) === this.scope.selectedPeriod
        )
    }

    #reloadDetail() {
        const { selectedEntityType, selectedEntityId, selectedPeriod } = this.scope
        return this.#loadDetail(selectedEntityType, selectedEntityId, selectedPeriod)
    }

    async #loadDetail(entityType?: BillingEntityType, entityId?: number, period?: string) {
        this.scope.selectedEntityType = entityId ? entityType : undefined
        this.scope.selectedEntityId = entityId
        this.scope.selectedPeriod = entityId ? period : undefined
        this.scope.selectedEntityName = entityId ? this.#entityName(entityType, entityId) : ''
        this.#syncSelectedLine()

        await Promise.all([this.#loadUserDetail(entityType, entityId, period), this.#loadOrigins()])
    }

    async #loadUserDetail(entityType?: BillingEntityType, entityId?: number, period?: string) {
        if (!entityId || !entityType) {
            this.scope.detailRows = []
            return
        }

        if (entityType === 'USER') {
            this.scope.detailRows = this.#allLines
                .filter(
                    (line) =>
                        line.entityType === 'USER' &&
                        line.entityId === entityId &&
                        (!period || competenciaKey(line.mesAno) === period)
                )
                .map(toOwnFranchiseDetail)
            return
        }

        try {
            this.scope.detailLoading = true
            this.scope.detailRows = await billingService.fetchUserBreakdown(entityId, this.#detailFilter(period))
        } catch (caught) {
            LOG.error('loadDetail', caught)
            this.scope.detailRows = []
        } finally {
            this.scope.detailLoading = false
        }
    }

    async #loadOrigins() {
        const { origins } = this.scope
        const request = ++this.#originsRequest

        origins.context = this.#originsContext()

        try {
            origins.loading = true
            const rows = await billingService.fetchOrigins(this.#originQuery())
            if (request === this.#originsRequest) {
                origins.rows = rows
            }
        } catch (caught) {
            LOG.error('loadOrigins', caught)
            if (request === this.#originsRequest) {
                origins.rows = []
            }
        } finally {
            if (request === this.#originsRequest) {
                origins.loading = false
            }
        }
    }

    #originQuery(): BillingOriginQuery {
        const { selectedEntityType, selectedEntityId, selectedPeriod, origins } = this.scope

        const view = { byUser: origins.byUser, byVersion: origins.byVersion }

        if (!selectedEntityId) {
            return { ...this.#currentFilter(), ...view }
        }

        return {
            ...this.#detailFilter(selectedPeriod),
            entityType: selectedEntityType,
            entityId: selectedEntityId,
            ...view
        }
    }

    #originsContext(): string {
        const { selectedEntityId, selectedEntityName, selectedPeriod } = this.scope
        const { month } = this.scope.filterPane

        if (selectedEntityId) {
            const period = selectedPeriod ?? month
            return period ? `${selectedEntityName} :: ${formatMonthLabel(period)}` : selectedEntityName
        }

        return month ? `Todos os registros :: ${formatMonthLabel(month)}` : 'Todos os registros do período'
    }

    #entityName(entityType: BillingEntityType | undefined, entityId: number): string {
        const fromLines = this.#allLines.find((line) => line.entityType === entityType && line.entityId === entityId)
        if (fromLines) {
            return fromLines.entityName
        }

        return entityType === 'GROUP'
            ? (this.scope.filterPane.groupOptions.find((option) => option.id === entityId)?.name ?? '')
            : ''
    }

    #rangeFilter(): BillingFilter {
        return {
            startMonth: this.#appliedStartMonth || undefined,
            endMonth: this.#appliedEndMonth || undefined,
            groupId: this.scope.filterPane.groupId
        }
    }

    #currentFilter(): BillingFilter {
        const filter = this.#rangeFilter()
        const { month } = this.scope.filterPane

        return month ? { ...filter, startMonth: month, endMonth: month } : filter
    }

    #detailFilter(period?: string): BillingFilter {
        const filter = this.#currentFilter()
        return period ? { ...filter, startMonth: period, endMonth: period } : filter
    }

    #syncIsFiltering() {
        const { filterPane } = this.scope
        filterPane.isFiltering = !!filterPane.groupId || !!filterPane.month || filterPane.onlyExceeded
    }

    private async handleToggleExpanded() {
        this.scope.filterPane.expanded = !this.scope.filterPane.expanded
    }

    private async handleStartMonthChanged(value: string) {
        this.scope.filterPane.startMonth = value
    }

    private async handleEndMonthChanged(value: string) {
        this.scope.filterPane.endMonth = value
    }

    private async handleMonthChanged(value?: string) {
        await this.#selectMonth(value || undefined)
    }

    private async handlePeriodClicked(period?: string) {
        await this.#selectMonth(period && period !== this.scope.filterPane.month ? period : undefined)
    }

    private async handleOnlyExceededChanged(value: boolean) {
        this.scope.filterPane.onlyExceeded = value

        this.#applyMonthSelection()
        this.#syncIsFiltering()
        this.updateHistory()

        await this.#reloadDetail()
    }

    private async handleGroupChanged(value?: number) {
        this.scope.filterPane.groupId = value
        this.#syncIsFiltering()
    }

    private async handleApply() {
        this.#syncIsFiltering()

        this.scope.selectedEntityType = this.scope.filterPane.groupId ? 'GROUP' : undefined
        this.scope.selectedEntityId = this.scope.filterPane.groupId
        this.scope.selectedPeriod = undefined

        this.updateHistory()
        await this.#load()
    }

    private async handleClear() {
        const { filterPane } = this.scope

        filterPane.startMonth = defaultStartMonth()
        filterPane.endMonth = currentMonth()
        filterPane.month = currentMonth()
        filterPane.groupId = undefined
        filterPane.onlyExceeded = false

        this.scope.selectedEntityType = undefined
        this.scope.selectedEntityId = undefined
        this.scope.selectedPeriod = undefined
        this.#syncIsFiltering()
        this.updateHistory()
        await this.#load()
    }

    private async handleSelectLine(entityType: BillingEntityType, entityId: number, mesAno: string) {
        const period = competenciaKey(mesAno)

        const isSame =
            this.scope.selectedEntityType === entityType &&
            this.scope.selectedEntityId === entityId &&
            this.scope.selectedPeriod === period

        await (isSame ? this.#loadDetail() : this.#loadDetail(entityType, entityId, period))
    }

    private async handleInvoiceSelected() {
        const line = this.scope.selectedLine

        if (!line) {
            return
        }

        if (line.valorCentavos === null) {
            this.app.notify('warning', 'A competência não tem valor apurado e não pode ser faturada.')
            return
        }

        this.#openInvoiceDialog('INVOICE', line)
    }

    private async handleReverseSelected() {
        const line = this.scope.selectedLine

        if (!line?.invoice) {
            return
        }

        this.#openInvoiceDialog('REVERSE', line)
    }

    #openInvoiceDialog(mode: 'INVOICE' | 'REVERSE', line: BillingLine) {
        const dialog = new BillingInvoiceDialogScope()

        dialog.mode = mode
        dialog.entityName = line.entityName
        dialog.competenciaLabel = formatCompetencia(line.mesAno)
        dialog.valorCalculado =
            (mode === 'REVERSE' ? (line.invoice?.valorCobradoCentavos ?? 0) : (line.valorCentavos ?? 0)) / 100
        dialog.descontoText = ''
        dialog.observacao = ''
        dialog.motivo = ''
        dialog.errorMessage = ''

        dialog.onDescontoChanged = this.action(this.handleDescontoChanged)
        dialog.onObservacaoChanged = this.action(this.handleObservacaoChanged)
        dialog.onMotivoChanged = this.action(this.handleMotivoChanged)
        dialog.onIsentarTotal = this.action(this.handleIsentarTotal)
        dialog.onCancel = this.action(this.handleCloseInvoiceDialog)
        dialog.onConfirm = this.action(this.handleConfirmInvoiceDialog)

        this.scope.invoiceDialog = dialog
    }

    private async handleDescontoChanged(value: string) {
        const dialog = this.scope.invoiceDialog
        if (dialog) {
            dialog.descontoText = value
            dialog.errorMessage = ''
        }
    }

    private async handleObservacaoChanged(value: string) {
        const dialog = this.scope.invoiceDialog
        if (dialog) {
            dialog.observacao = value
        }
    }

    private async handleMotivoChanged(value: string) {
        const dialog = this.scope.invoiceDialog
        if (dialog) {
            dialog.motivo = value
        }
    }

    private async handleIsentarTotal() {
        const dialog = this.scope.invoiceDialog
        if (dialog) {
            dialog.descontoText = dialog.valorCalculado.toFixed(2)
            dialog.errorMessage = ''
        }
    }

    private async handleCloseInvoiceDialog() {
        this.scope.invoiceDialog = undefined
    }

    private async handleConfirmInvoiceDialog() {
        const dialog = this.scope.invoiceDialog
        const line = this.scope.selectedLine

        if (!dialog || !line || dialog.saving) {
            return
        }

        const competencia = competenciaKey(line.mesAno)

        if (!competencia) {
            return
        }

        try {
            dialog.saving = true
            dialog.errorMessage = ''

            if (dialog.mode === 'REVERSE') {
                await billingService.reverseInvoice({
                    entityType: line.entityType,
                    entityId: line.entityId,
                    mesAno: competencia,
                    motivo: dialog.motivo
                })
            } else {
                const desconto = parseNonNegativeDecimal(dialog.descontoText) ?? 0

                if (desconto > dialog.valorCalculado) {
                    dialog.errorMessage = 'O desconto não pode passar do valor apurado.'
                    return
                }

                await billingService.invoiceLine({
                    entityType: line.entityType,
                    entityId: line.entityId,
                    mesAno: competencia,
                    descontoCentavos: toCentavos(desconto),
                    observacao: dialog.observacao
                })
            }

            this.scope.invoiceDialog = undefined
            this.app.notify('success', dialog.mode === 'REVERSE' ? 'Faturamento estornado.' : 'Competência faturada.')

            await this.#load()
        } catch (caught) {
            LOG.error('confirmInvoice', caught)
            dialog.errorMessage = (caught as Error)?.message || 'Não foi possível concluir a operação.'
        } finally {
            dialog.saving = false
        }
    }

    private async handleOriginsByUserChanged(value: boolean) {
        const { origins } = this.scope

        if (origins.byUser === value) {
            return
        }

        origins.byUser = value
        await this.#loadOrigins()
    }

    private async handleOriginsByVersionChanged(value: boolean) {
        const { origins } = this.scope

        if (origins.byVersion === value) {
            return
        }

        origins.byVersion = value
        await this.#loadOrigins()
    }

    private async handleOriginsDownload() {
        const { origins } = this.scope

        if (origins.downloading) {
            return
        }

        try {
            origins.downloading = true

            const query = this.#originQuery()
            const blob = await billingService.downloadOriginsXlsx(query)

            const periodo =
                query.startMonth === query.endMonth ? query.startMonth : `${query.startMonth}-a-${query.endMonth}`

            downloadBlobAsFile(blob, `exportacoes-por-origem-${periodo}.xlsx`)
        } catch (caught) {
            LOG.error('originsDownload', caught)
            this.app.notify('error', 'Não foi possível gerar a planilha de exportações por origem')
        } finally {
            origins.downloading = false
        }
    }

    private async handleDownload() {
        if (this.scope.downloading) {
            return
        }

        try {
            this.scope.downloading = true

            const filter = this.#currentFilter()
            const blob = await billingService.downloadXlsx(filter)

            const periodo =
                filter.startMonth === filter.endMonth ? filter.startMonth : `${filter.startMonth}-a-${filter.endMonth}`

            downloadBlobAsFile(blob, `faturamento-excedentes-${periodo}.xlsx`)
        } catch (caught) {
            LOG.error('download', caught)
            this.app.notify('error', 'Não foi possível gerar a planilha de faturamento')
        } finally {
            this.scope.downloading = false
        }
    }
}

function toOwnFranchiseDetail(line: BillingLine): BillingUserLine {
    return {
        mesAno: line.mesAno,
        groupId: line.contractGroupId ?? 0,
        userId: line.entityId,
        userName: line.entityName,
        userStatus: line.userDeletedDate ? 'DELETED' : 'ACTIVE',
        qtdExportada: line.qtdExportada,
        qtdExportacoes: line.qtdExportacoes,
        ultimaExportacao: line.ultimaExportacao,
        participacaoPct: line.qtdExportada > 0 ? 100 : 0,
        qtdExcedenteCronologico: line.qtdExcedente,
        valorRateadoCentavos: line.valorCentavos,
        valorRateado: line.valor
    }
}

type PeriodAccumulator = {
    period: string
    qtdExcedente: number
    abertoCentavos: number
    faturadoCentavos: number
    cortesiaCentavos: number
}

function buildPeriodRows(lines: BillingLine[]): BillingPeriodRow[] {
    const byPeriod = new Map<string, PeriodAccumulator>()

    for (const line of lines) {
        const period = competenciaKey(line.mesAno) ?? ''
        const row = byPeriod.get(period) ?? {
            period,
            qtdExcedente: 0,
            abertoCentavos: 0,
            faturadoCentavos: 0,
            cortesiaCentavos: 0
        }

        row.qtdExcedente += line.qtdExcedente

        if (line.invoice) {
            row.faturadoCentavos += line.invoice.valorCobradoCentavos
            row.cortesiaCentavos += line.invoice.descontoCentavos
        } else {
            row.abertoCentavos += line.valorCentavos ?? 0
        }

        byPeriod.set(period, row)
    }

    return [...byPeriod.values()]
        .sort((a, b) => a.period.localeCompare(b.period))
        .map((row) => ({
            period: row.period,
            qtdExcedente: row.qtdExcedente,
            valor: (row.abertoCentavos + row.faturadoCentavos + row.cortesiaCentavos) / 100,
            valorAberto: row.abertoCentavos / 100,
            valorFaturado: row.faturadoCentavos / 100,
            valorCortesia: row.cortesiaCentavos / 100
        }))
}

function summarize(lines: BillingLine[]) {
    const entidade = (line: BillingLine) => `${line.entityType}:${line.entityId}`

    const comExcedente = new Set(lines.filter((line) => line.qtdExcedente > 0).map(entidade))
    const semTabela = new Set(lines.filter((line) => line.status === 'SEM_TABELA').map(entidade))

    const faturadas = lines.filter((line) => !!line.invoice)
    const valorFaturadoCentavos = faturadas.reduce((acc, line) => acc + (line.invoice?.valorCobradoCentavos ?? 0), 0)
    const valorAbertoCentavos = lines.reduce((acc, line) => acc + (line.invoice ? 0 : (line.valorCentavos ?? 0)), 0)

    const comCortesia = faturadas.filter((line) => (line.invoice?.descontoCentavos ?? 0) > 0)
    const valorCortesiaCentavos = comCortesia.reduce((acc, line) => acc + (line.invoice?.descontoCentavos ?? 0), 0)

    return {
        qtdExportada: lines.reduce((acc, line) => acc + line.qtdExportada, 0),
        qtdExcedente: lines.reduce((acc, line) => acc + line.qtdExcedente, 0),
        valorAFaturar: valorAbertoCentavos / 100,
        valorFaturado: valorFaturadoCentavos / 100,
        linhasFaturadas: faturadas.length,
        valorCortesia: valorCortesiaCentavos / 100,
        linhasComCortesia: comCortesia.length,
        entidadesComExcedente: comExcedente.size,
        entidadesSemTabela: semTabela.size
    }
}

function enumerateMonths(start: string, end: string): string[] {
    if (!start || !end || start > end) {
        return []
    }

    const months: string[] = []
    const cursor = new Date(Number(start.slice(0, 4)), Number(start.slice(5, 7)) - 1, 1)

    while (months.length < MAX_MONTH_OPTIONS) {
        const month = toMonthInput(cursor)
        if (month > end) {
            break
        }
        months.push(month)
        cursor.setMonth(cursor.getMonth() + 1)
    }

    return months
}

function currentMonth(): string {
    return toMonthInput(new Date())
}

function defaultStartMonth(): string {
    const date = new Date()
    date.setMonth(date.getMonth() - (DEFAULT_RANGE_MONTHS - 1))
    return toMonthInput(date)
}

function toMonthInput(date: Date): string {
    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}`
}

export default BillingPresenter
